/**
 * Runbook · Deploy USDC-Bound KojiruACSHook to Base Mainnet
 *
 * Interactive step-by-step page. Every command has a copy button. Progress
 * is persisted to localStorage so Wayne can pause and resume across sessions.
 * Same design pattern as the /kypa/instrument mint sandbox.
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Copy, Check, Download, ExternalLink, ShieldCheck, AlertTriangle, RotateCcw } from 'lucide-react';

const LS_KEY = 'kojiru.runbook.usdc-hook.progress.v1';

function CopyBlock({ id, cmd, note }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(cmd);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };
  return (
    <div className="my-3">
      <div className="relative bg-black/60 border border-white/10 rounded-lg overflow-hidden">
        <button
          onClick={doCopy}
          className="absolute top-2 right-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/[0.08] hover:bg-white/[0.15] text-[10px] font-bold tracking-wider text-white uppercase transition"
          data-testid={`copy-${id}`}
        >
          {copied ? <><Check className="w-3 h-3" /> copied</> : <><Copy className="w-3 h-3" /> copy</>}
        </button>
        <pre className="text-[12px] font-mono text-slate-200 leading-relaxed p-4 pr-20 overflow-x-auto whitespace-pre-wrap">{cmd}</pre>
      </div>
      {note && <div className="text-[11px] text-slate-500 mt-1 italic">{note}</div>}
    </div>
  );
}

function StepCard({ n, title, done, onToggle, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`bg-[#0f0f16] border rounded-xl p-5 ${done ? 'border-emerald-500/40 bg-emerald-500/[0.03]' : 'border-white/10'}`}
      data-testid={`step-${n}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <button
          onClick={onToggle}
          className={`flex-shrink-0 w-6 h-6 rounded-md border-2 transition inline-flex items-center justify-center ${done ? 'border-emerald-400 bg-emerald-400/20' : 'border-white/25 hover:border-white/50'}`}
          data-testid={`step-${n}-toggle`}
          aria-label={done ? 'mark step incomplete' : 'mark step complete'}
        >
          {done && <Check className="w-4 h-4 text-emerald-300" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold tracking-[1.5px] text-slate-500 mb-1">STEP {n}</div>
          <h3 className={`text-lg sm:text-xl font-bold leading-tight ${done ? 'text-slate-400 line-through' : 'text-white'}`}>
            {title}
          </h3>
        </div>
      </div>
      <div className="pl-9 space-y-3 text-sm text-slate-300 leading-relaxed">
        {children}
      </div>
    </motion.div>
  );
}

const ADDRESSES = {
  usdc: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
  arbiter: '0xfba264c34d19CbFf2f51Cf719E702bc44822994d',
  signer: '0xDCB21cC5Fc3d335D79717aF2C2404F5c622c3441',
  oldHook: '0x16C72cbcA5B517D3E9b2262d29A5B7701D294476',
  oldOracle: '0x93BF92B6818E4Acf20028dF993B246655413B74e',
};

export default function RunbookUsdcHook() {
  const [done, setDone] = useState({});
  const [newHook, setNewHook] = useState('');
  const [newOracle, setNewOracle] = useState('');

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setDone(parsed.done || {});
        setNewHook(parsed.newHook || '');
        setNewOracle(parsed.newOracle || '');
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ done, newHook, newOracle }));
    } catch { /* ignore */ }
  }, [done, newHook, newOracle]);

  const toggle = (n) => setDone((d) => ({ ...d, [n]: !d[n] }));
  const reset = () => { if (window.confirm('Reset all progress?')) { setDone({}); setNewHook(''); setNewOracle(''); } };

  const totalSteps = 8;
  const completedCount = Object.values(done).filter(Boolean).length;
  const pct = Math.round((completedCount / totalSteps) * 100);

  const nh = newHook || '<NEW_HOOK>';
  const no = newOracle || '<NEW_ORACLE>';

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Helmet>
        <title>Runbook · Deploy USDC Hook · Kojiru Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Navbar />

      <section className="pt-28 pb-8 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="text-[10px] font-bold tracking-[2px] text-emerald-400 mb-4">
            RUNBOOK · MAINNET · ADMIN ACTION REQUIRED
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.05] tracking-tight mb-4">
            Deploy USDC-bound KojiruACSHook.
          </h1>
          <p className="text-slate-300 leading-relaxed max-w-3xl mb-6">
            Eight steps. ~30 minutes. ~0.005 Base ETH (≈ $20 USD) in gas. Every command below has a copy button.
            Progress saves automatically to this browser. Tick each step as you complete it locally.
          </p>

          {/* Progress bar */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1 h-2 bg-white/[0.08] rounded-full overflow-hidden">
              <div className="h-full bg-emerald-400 transition-all" style={{ width: `${pct}%` }} data-testid="progress-bar" />
            </div>
            <div className="text-xs text-slate-400 tabular-nums whitespace-nowrap">
              {completedCount}/{totalSteps} · {pct}%
            </div>
            <button onClick={reset} className="text-xs text-slate-500 hover:text-white inline-flex items-center gap-1" data-testid="reset-progress">
              <RotateCcw className="w-3 h-3" /> reset
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            <a href="/downloads/mainnet/USDC_HOOK_DEPLOYMENT_RUNSHEET.md" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/5">
                <Download className="w-3.5 h-3.5 mr-2" /> Full runbook (markdown)
              </Button>
            </a>
            <a href="/downloads/mainnet/KojiruACSHook.sol" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/5">
                <Download className="w-3.5 h-3.5 mr-2" /> KojiruACSHook.sol
              </Button>
            </a>
            <a href="/downloads/mainnet/verify_usdc_hook.py" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/5">
                <Download className="w-3.5 h-3.5 mr-2" /> verify_usdc_hook.py
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Reference addresses */}
      <section className="py-6 px-6 border-b border-white/5 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto">
          <div className="text-[10px] font-bold tracking-[1.5px] text-slate-500 mb-2">REFERENCE · BASE MAINNET (chain 8453)</div>
          <div className="grid sm:grid-cols-2 gap-2 text-xs">
            {[
              ['USDC (Base)', ADDRESSES.usdc],
              ['Arbiter (final owner)', ADDRESSES.arbiter],
              ['Existing ACS signer', ADDRESSES.signer],
              ['Old KOJI-bound Hook', ADDRESSES.oldHook],
              ['Old Oracle', ADDRESSES.oldOracle],
            ].map(([label, addr]) => (
              <div key={label} className="flex items-center gap-3 bg-black/40 border border-white/5 rounded px-3 py-2">
                <div className="text-slate-500 text-[10px] uppercase tracking-wider w-32 flex-shrink-0">{label}</div>
                <div className="font-mono text-slate-200 truncate flex-1">{addr}</div>
                <button
                  onClick={() => navigator.clipboard.writeText(addr)}
                  className="text-slate-500 hover:text-white flex-shrink-0"
                  title="copy"
                  data-testid={`copy-addr-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-10 px-6">
        <div className="max-w-4xl mx-auto space-y-5">

          {/* Step 0 – prereqs */}
          <StepCard n={0} title="Prerequisites (once, ~5 min)" done={done[0]} onToggle={() => toggle(0)}>
            <ul className="space-y-2 list-disc pl-5">
              <li><strong>Base ETH</strong> ≥ 0.005 (≈ $20 USD) on your deployer wallet. Bridge at <a href="https://bridge.base.org" target="_blank" rel="noopener noreferrer" className="text-cyan-300 underline inline-flex items-center gap-1">bridge.base.org <ExternalLink className="w-3 h-3" /></a> if low.</li>
              <li><strong>BaseScan API key</strong> (free): <a href="https://basescan.org/myapikey" target="_blank" rel="noopener noreferrer" className="text-cyan-300 underline inline-flex items-center gap-1">basescan.org/myapikey <ExternalLink className="w-3 h-3" /></a></li>
              <li><strong>Foundry</strong> installed locally:</li>
            </ul>
            <CopyBlock id="foundry" cmd={`curl -L https://foundry.paradigm.xyz | bash && foundryup`} />
          </StepCard>

          {/* Step 1 – workspace */}
          <StepCard n={1} title="Set up your workspace (~2 min)" done={done[1]} onToggle={() => toggle(1)}>
            <p>Create a clean folder and pull the contract source directly from Kojiru:</p>
            <CopyBlock
              id="workspace"
              cmd={`mkdir -p ~/kojiru-usdc-hook && cd ~/kojiru-usdc-hook
forge init --force --no-commit
curl -o src/KojiruACSHook.sol https://kojiru.com/downloads/mainnet/KojiruACSHook.sol
forge install openzeppelin/openzeppelin-contracts --no-commit
forge remappings > remappings.txt`}
            />
          </StepCard>

          {/* Step 2 – export env */}
          <StepCard n={2} title="Set your deployment env vars (once, this shell session)" done={done[2]} onToggle={() => toggle(2)}>
            <p>Replace <code className="text-emerald-300">0x&lt;your deployer PK&gt;</code> and <code className="text-emerald-300">&lt;your BaseScan key&gt;</code>. Do NOT paste these anywhere else.</p>
            <CopyBlock
              id="env"
              cmd={`export DEPLOYER_PK=0x<your deployer PK>
export BASESCAN_KEY=<your BaseScan API key>

export BASE_RPC=https://mainnet.base.org
export USDC=${ADDRESSES.usdc}
export ARBITER=${ADDRESSES.arbiter}
export TEMP_ORACLE=${ADDRESSES.oldOracle}
export SIGNER=${ADDRESSES.signer}`}
            />
          </StepCard>

          {/* Step 3 – deploy Hook */}
          <StepCard n={3} title="Deploy the new Hook (bound to USDC, temp oracle)" done={done[3]} onToggle={() => toggle(3)}>
            <p>This deploys with the existing Oracle as a placeholder. We swap it in step 5.</p>

            <div className="border border-cyan-500/25 rounded-md p-3 bg-cyan-500/[0.04] text-xs text-slate-300 space-y-2">
              <div className="text-cyan-300 font-bold text-[11px] tracking-widest">SANITY CHECK — RUN THIS FIRST</div>
              <p>Confirm the three env vars are actually set in the current MINGW64 session. Each length must print <code className="text-cyan-300">42</code>. If any is <code className="text-red-300">0</code>, re-run the <code className="text-cyan-300">export</code> block from Step 1 in the same terminal window.</p>
            </div>
            <CopyBlock
              id="check-envs"
              cmd={`echo "USDC=[$USDC] len=\${#USDC}"
echo "ARBITER=[$ARBITER] len=\${#ARBITER}"
echo "TEMP_ORACLE=[$TEMP_ORACLE] len=\${#TEMP_ORACLE}"
echo "BASE_RPC=[$BASE_RPC] len=\${#BASE_RPC}"
echo "BASESCAN_KEY set? len=\${#BASESCAN_KEY}"
echo "DEPLOYER_PK set? len=\${#DEPLOYER_PK}  (should be 64 or 66)"`}
            />

            <CopyBlock
              id="deploy-hook"
              cmd={`forge create src/KojiruACSHook.sol:KojiruACSHook \\
  --rpc-url "$BASE_RPC" \\
  --private-key "$DEPLOYER_PK" \\
  --chain 8453 \\
  --broadcast \\
  --verify --etherscan-api-key "$BASESCAN_KEY" \\
  --constructor-args "$USDC" "$ARBITER" "$TEMP_ORACLE"`}
              note="When the output says 'Deployed to: 0x…', paste that address into the field below."
            />

            <div className="border border-amber-500/30 rounded-md p-3 bg-amber-500/[0.05] text-xs text-slate-300 space-y-2 mt-3">
              <div className="text-amber-300 font-bold text-[11px] tracking-widest">IF YOU SEE NO OUTPUT AT ALL</div>
              <p>On Windows MINGW64, backslash line-continuations sometimes break on CRLF line endings — the shell silently runs only the first line. Use this <strong>single-line</strong> version instead (no backslashes, no continuations):</p>
            </div>
            <CopyBlock
              id="deploy-hook-oneline"
              cmd={`forge create src/KojiruACSHook.sol:KojiruACSHook --rpc-url "$BASE_RPC" --private-key "$DEPLOYER_PK" --chain 8453 --broadcast --verify --etherscan-api-key "$BASESCAN_KEY" --constructor-args "$USDC" "$ARBITER" "$TEMP_ORACLE"`}
              note={"After it returns (or hangs > 90s), immediately run:  echo \"exit=$?\"   — non-zero means forge failed. If 0 but no address, forge is still submitting verification (safe to wait)."}
            />

            <div className="border border-amber-500/30 rounded-md p-3 bg-amber-500/[0.05] text-xs text-slate-300 space-y-2 mt-3">
              <div className="text-amber-300 font-bold text-[11px] tracking-widest">IF THAT STILL PRINTS NOTHING</div>
              <p>Isolate deployment from BaseScan verification — drop <code className="text-amber-300">--verify</code> and <code className="text-amber-300">--etherscan-api-key</code>. Verification can be re-run separately with <code className="text-amber-300">forge verify-contract</code> once we have the address.</p>
            </div>
            <CopyBlock
              id="deploy-hook-noverify"
              cmd={`forge create src/KojiruACSHook.sol:KojiruACSHook --rpc-url "$BASE_RPC" --private-key "$DEPLOYER_PK" --chain 8453 --broadcast --constructor-args "$USDC" "$ARBITER" "$TEMP_ORACLE" 2>&1 | tee /tmp/forge-hook-deploy.log; echo "exit=$?"`}
              note="Prints Deployer / Deployed to / Transaction hash and also writes the whole session to /tmp/forge-hook-deploy.log — send Wayne that file if it still misbehaves."
            />

            <label className="block">
              <span className="text-xs text-slate-400 mb-1 block">NEW_HOOK address (paste here):</span>
              <input
                type="text"
                value={newHook}
                onChange={(e) => setNewHook(e.target.value.trim())}
                placeholder="0x…"
                className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm font-mono text-white focus:border-emerald-500/50 focus:outline-none"
                data-testid="input-new-hook"
              />
            </label>
            {newHook && (
              <>
                <div className="border border-emerald-500/25 rounded-md p-3 bg-emerald-500/[0.04] text-xs text-slate-300 space-y-2 mt-3">
                  <div className="text-emerald-300 font-bold text-[11px] tracking-widest">CHECKPOINT — SAVE ALL ENV VARS TO A FILE</div>
                  <p>Windows Git Bash drops env vars whenever a new terminal tab opens or the shell recycles. Write them to a file now so if the session dies you can <code className="text-emerald-300">source</code> them back in one command instead of re-exporting seven values.</p>
                </div>
                <CopyBlock
                  id="checkpoint-env"
                  cmd={`cat > ~/kojiru-usdc-hook.env <<'EOF'
export BASE_RPC="$BASE_RPC"
export DEPLOYER_PK="$DEPLOYER_PK"
export BASESCAN_KEY="$BASESCAN_KEY"
export USDC="$USDC"
export ARBITER="$ARBITER"
export TEMP_ORACLE="$TEMP_ORACLE"
export SIGNER="$SIGNER"
export NEW_HOOK="${newHook}"
EOF
echo "\u2713 saved  \u2014  if the terminal dies, restore with:  source ~/kojiru-usdc-hook.env"`}
                  note="Run again after Step 4 to add NEW_ORACLE to the same file."
                />
                <CopyBlock
                  id="verify-hook-token"
                  cmd={`cast call ${newHook} "kojiTokenContract()(address)" --rpc-url $BASE_RPC
# MUST return ${ADDRESSES.usdc}`}
                />
              </>
            )}
          </StepCard>

          {/* Step 4 – deploy Oracle */}
          <StepCard n={4} title="Deploy the new Oracle bound to the new Hook" done={done[4]} onToggle={() => toggle(4)}>
            {!newHook && <div className="text-amber-300 text-xs inline-flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5" /> Paste NEW_HOOK in Step 3 first — this command uses it.</div>}
            <CopyBlock
              id="deploy-oracle"
              cmd={`forge create src/KojiruACSHook.sol:KojiruACSOracle \\
  --rpc-url "$BASE_RPC" \\
  --private-key "$DEPLOYER_PK" \\
  --chain 8453 \\
  --broadcast \\
  --verify --etherscan-api-key "$BASESCAN_KEY" \\
  --constructor-args "${nh}" "$SIGNER"`}
            />
            <label className="block">
              <span className="text-xs text-slate-400 mb-1 block">NEW_ORACLE address (paste here):</span>
              <input
                type="text"
                value={newOracle}
                onChange={(e) => setNewOracle(e.target.value.trim())}
                placeholder="0x…"
                className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm font-mono text-white focus:border-emerald-500/50 focus:outline-none"
                data-testid="input-new-oracle"
              />
            </label>
          </StepCard>

          {/* Step 5 – swap oracle */}
          <StepCard n={5} title="Rebind the Hook to the new Oracle" done={done[5]} onToggle={() => toggle(5)}>
            {(!newHook || !newOracle) && <div className="text-amber-300 text-xs inline-flex items-center gap-2 mb-2"><AlertTriangle className="w-3.5 h-3.5" /> Paste both addresses above first.</div>}
            <CopyBlock
              id="swap-oracle"
              cmd={`cast send ${nh} "setOracle(address)" ${no} \\
  --rpc-url $BASE_RPC \\
  --private-key $DEPLOYER_PK`}
            />
            <p className="text-xs text-slate-400">Verify the swap:</p>
            <CopyBlock
              id="verify-swap"
              cmd={`cast call ${nh} "oracle()(address)" --rpc-url $BASE_RPC
# MUST return ${no}`}
            />
          </StepCard>

          {/* Step 6 – ownership transfer */}
          <StepCard n={6} title="Transfer ownership to the arbiter (hygiene, recommended)" done={done[6]} onToggle={() => toggle(6)}>
            <p>The deployer wallet currently owns both contracts. Transfer to the arbiter (or a multisig if you have one).</p>
            <CopyBlock
              id="transfer-hook"
              cmd={`cast send ${nh} "transferOwnership(address)" $ARBITER \\
  --rpc-url $BASE_RPC --private-key $DEPLOYER_PK

cast send ${no} "transferOwnership(address)" $ARBITER \\
  --rpc-url $BASE_RPC --private-key $DEPLOYER_PK`}
            />
          </StepCard>

          {/* Step 7 – verify */}
          <StepCard n={7} title="Run the seven-check verifier" done={done[7]} onToggle={() => toggle(7)}>
            <p>One command. Prints all seven cross-checks in green if the deployment is production-ready.</p>
            <CopyBlock
              id="verifier"
              cmd={`curl -o verify_usdc_hook.py https://kojiru.com/downloads/mainnet/verify_usdc_hook.py
pip install web3 eth-account
NEW_HOOK=${nh} NEW_ORACLE=${no} python verify_usdc_hook.py`}
            />
            <p className="text-xs text-slate-500 italic">If any check is red, stop &mdash; do not update env vars until all seven pass.</p>
          </StepCard>

          {/* Step 8 – ping */}
          <StepCard n={8} title="Ping me with the two new addresses" done={done[8]} onToggle={() => toggle(8)}>
            <p>Paste this in the chat and I'll do the rest:</p>
            <CopyBlock
              id="handoff"
              cmd={`USDC Hook deployment complete.
NEW_HOOK=${nh}
NEW_ORACLE=${no}
Please update frontend/.env + backend/.env, restart backend, and confirm the packet-producer disclaimer is gone.`}
            />
            <p className="text-xs text-slate-400">I'll then:</p>
            <ul className="list-disc pl-5 space-y-1 text-xs text-slate-400">
              <li>Update <code>REACT_APP_ACS_HOOK_ADDRESS</code> and <code>REACT_APP_ACS_ORACLE_ADDRESS</code> in <code>frontend/.env</code></li>
              <li>Update <code>ACS_HOOK_ADDRESS</code>, <code>INDEXER_BASE_HOOK</code>, and <code>INDEXER_BASE_DEPLOY_BLOCK</code> in <code>backend/.env</code></li>
              <li>Restart the backend</li>
              <li>Confirm <code>/api/indexer/status</code> shows the new Hook address and packets produced going forward carry the <code>USDC</code> symbol with no disclaimer</li>
            </ul>
          </StepCard>
        </div>
      </section>

      {/* Safety */}
      <section className="py-8 px-6 border-t border-white/5 bg-amber-500/[0.03]">
        <div className="max-w-4xl mx-auto flex gap-4">
          <ShieldCheck className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-[10px] font-bold tracking-[1.5px] text-amber-300 mb-1">SAFETY NOTES</div>
            <ul className="text-sm text-slate-300 space-y-1 list-disc pl-5 leading-relaxed">
              <li>Every command runs on <em>your</em> machine. This page never sees your private key.</li>
              <li>Chain-id is locked to 8453 in the verifier &mdash; if you accidentally point at Sepolia, it refuses.</li>
              <li>The old Hook and Oracle remain source-verified on BaseScan &mdash; not deleted, deprecated.</li>
              <li>Do not run this on Sepolia. Sepolia deployment already exists and is fine.</li>
            </ul>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
